import * as React from 'react';
import { useEffect, useState } from 'react';
import styles from './pref_code_repositories.module.css';
import { getLogger } from '../../services/logger.service';

import memberStore from '../../stores/member.store';
import { observer } from 'mobx-react';
import { IGithubRepo } from '../../models/github.model';
import githubConnection from '../../services/connection/github.connection';
import Popup, { PopupInfo } from '../common/popup.component';
import Select from '../common/select.component';

import trash from '../../img/preferences/trash.svg';

const log = getLogger('PrefCodeRepositories');

const GithubProvider = observer(() => {
  const account = memberStore.account;
  const integration = account?.githubIntegration;
  const [repos, setRepos] = useState<IGithubRepo[]>([]);
  const [branches, setBranches] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!integration) {
      setRepos([]);
      return;
    }
    githubConnection.listRepos()
      .then(setRepos)
      .catch(err => log.e('failed loading github repos', err));
  }, [integration?.installationId]);

  useEffect(() => {
    if (!integration) return;
    for (const mapping of integration.repoMappings) {
      const fullName = `${mapping.owner}/${mapping.repo}`;
      if (branches[fullName]) continue;
      githubConnection.listBranches(mapping.owner, mapping.repo)
        .then(list => setBranches(prev => ({...prev, [fullName]: list})))
        .catch(err => log.e('failed loading github branches', err));
    }
  }, [integration?.repoMappings.length]);

  const connect = async () => {
    const {url} = await githubConnection.getAuthUrl();
    const left = (window.innerWidth - 600) / 2;
    window.open(url, '_new', 'left=' + left + ',resizable=no,height=750,width=600,menubar=no,status=no');
  };

  const disconnect = () => {
    const info: PopupInfo = {
      title: 'Disconnect',
      msg: `Are you sure that you want to disconnect GitHub (${integration!.accountLogin}) from the account?`,
      ok: 'Disconnect',
      okCallback: async () => {
        await memberStore.disconnectGithub();
      },
      cancel: 'Cancel'
    };
    Popup.create(info);
  };

  const changeRepo = async (appId: string, fullName: string) => {
    if (!fullName) {
      await memberStore.removeGithubRepoMapping(appId);
      return;
    }
    const repo = repos.find(r => r.fullName === fullName)!;
    await memberStore.setGithubRepoMapping(appId, {owner: repo.owner, repo: repo.name, branch: repo.defaultBranch});
  };

  const changeBranch = async (appId: string, branch: string) => {
    const mapping = integration!.repoMappings.find(m => m.app === appId)!;
    await memberStore.setGithubRepoMapping(appId, {owner: mapping.owner, repo: mapping.repo, branch});
  };

  const repoValues: Record<string, string> = {'': 'None'};
  repos.forEach(r => repoValues[r.fullName] = r.fullName);

  return (
    <div className={styles.provider}>
      <div className={styles.providerHeader}>
        <div className={styles.providerName}>GitHub</div>
        {!integration && <button className={styles.connectButton} onClick={connect}>Connect GitHub</button>}
      </div>
      {integration &&
        <div>
          <div className={styles.connectedRow}>
            <div className={styles.connectedLabel}>Connected to <b>{integration.accountLogin}</b></div>
            <img alt="trash" className={styles.trash} src={trash} onClick={disconnect} />
          </div>
          {account!.apps.map(app => {
            const mapping = integration.repoMappings.find(m => m.app === app._id);
            const fullName = mapping ? `${mapping.owner}/${mapping.repo}` : '';
            const branchValues: Record<string, string> = {};
            (branches[fullName] ?? (mapping ? [mapping.branch] : [])).forEach(b => branchValues[b] = b);
            return (
              <div key={app._id} className={styles.repoRow}>
                <div className={styles.appName}>{app.name}</div>
                <Select
                  value={fullName}
                  nameValues={repoValues}
                  onChange={(value: string) => changeRepo(app._id, value)} />
                {mapping &&
                  <Select
                    value={mapping.branch}
                    nameValues={branchValues}
                    onChange={(value: string) => changeBranch(app._id, value)} />}
              </div>
            );
          })}
        </div>}
    </div>
  );
});

const PrefCodeRepositories = () => {
  return (
    <div className={styles.component}>
      <div className={styles.title}>
        Code Repositories
      </div>
      <div className={styles.subTitle}>
        Connect the repository that holds your app&apos;s code so Shipbook&apos;s AI can read it.
        LogChat answers with your actual source, and Loglytics issues and automatic alerts include
        where the problem is in the code and a suggested fix. Map each app to its repository below.
      </div>
      <div className={styles.section}>
        <GithubProvider />
        <div className={styles.underline} />
        <div className={styles.provider}>
          <div className={styles.providerHeader}>
            <div className={styles.providerName}>GitLab</div>
            <div className={styles.comingSoon}>Coming soon</div>
          </div>
        </div>
        <div className={styles.underline} />
        <div className={styles.provider}>
          <div className={styles.providerHeader}>
            <div className={styles.providerName}>Bitbucket</div>
            <div className={styles.comingSoon}>Coming soon</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(PrefCodeRepositories);
